/**
 * AiSummaryPage — "סכם סדרת טיפולים בעזרת AI": one patient's locked treatment
 * reports → AI-written summary → therapist edits → branded PDF.
 * Route: /dashboard/patients/:patientId/ai-summary[?reportId=…]
 *
 * Two panes (PdfExportShell): the sidebar holds the treatments picker, AI
 * notes + generate, the TipTap editor, document texts and the shared layout
 * toggles; the paper preview is read-only and re-renders the editor HTML.
 * `?reportId=` reopens a previously exported row (history pencil) as a new
 * revision (sourceReportId chain). Toggles are remembered on the therapist doc
 * (`treatmentPdfPrefs`, shared with the single-treatment export).
 *
 * Server: generateTreatmentsReportSummary (AI draft) + generateTreatmentsSummaryPdf.
 * Error codes: CK-3972 (generate), CK-3973 (export), CK-3974 (signature save),
 * CK-3976 (reopen report), CK-3981 (prefs save), CK-3982 (seed load).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Sparkle,
  FilePdf,
  WarningCircle,
  FileX,
  ArrowCounterClockwise,
  PencilSimpleLine,
} from "@phosphor-icons/react";
import {
  selectClinicId,
  selectClinicProfile,
  selectTherapistName,
  selectCanExportData,
  selectIsAdmin,
  selectIsTrialExpired,
  selectHasPermission,
} from "../../store/slices/authSlice";
import { useGetTreatmentsForPatientQuery } from "../../store/services/treatmentsApi";
import { useGetPatientQuery } from "../../store/services/patientsApi";
import { useGetCategoriesQuery } from "../../store/services/tasksApi";
import {
  useGenerateTreatmentsReportSummaryMutation,
  useGenerateTreatmentsSummaryPdfMutation,
  useGetGeneratedReportQuery,
} from "../../store/services/patientReportsApi";
import { logActivity, ACTIVITY_ACTIONS, ENTITY_TYPES } from "../../firebase";
import { getEffectiveLimits } from "../../services/planLimits";
import usePlanPermissions from "../../hooks/usePlanPermissions";
import useCanListAllPatientTreatments from "../../hooks/useCanListAllPatientTreatments";
import { formatHebrewDate } from "../../utils/dateUtils";
import LoaderLoop from "../../components/shared/loader/LoaderLoop";
import ContentLoader from "../../components/shared/ContentLoader";
import SignaturePadModal from "../../components/ui/SignaturePadModal";
import PdfPreviewModal from "../../components/dashboard/reports/patientReports/PdfPreviewModal";
import PdfExportShell from "../../components/pdfExport/PdfExportShell";
import { usePdfExportSession } from "../../components/pdfExport/usePdfExportSession";
import {
  Group,
  ToggleRow,
  TextField,
  SignatureSegment,
  LetterheadToggles,
  PatientToggles,
} from "../../components/pdfExport/PdfExportControls";
import { normalizeOptions, mergePrefs, TEXT_LIMITS } from "../../components/pdfExport/pdfExportOptions";
import {
  trim,
  buildLetterheadModel,
  buildPatientRows,
  buildSignatureModel,
  resolveLicenseNumber,
  therapistFullName,
  patientFullName,
} from "../../components/pdfExport/buildPaperModel";
import { sanitizeFieldHtml } from "../../components/treatments/export/treatmentFieldValue";
import TreatmentsTimelinePicker, {
  MAX_SELECTABLE_TREATMENTS,
} from "../../components/patients/aiSummary/TreatmentsTimelinePicker";
import AiNotesPanel from "../../components/patients/aiSummary/AiNotesPanel";
import SummaryEditor from "../../components/patients/aiSummary/SummaryEditor";
import AiSummaryPreview from "../../components/patients/aiSummary/AiSummaryPreview";
import { useAiSummaryState } from "../../components/patients/aiSummary/useAiSummaryState";
import "./AiSummaryPage.css";

const REPORT_TYPE_ID = "aiTreatmentsSummary";
const defaultTitleFor = (patientName) =>
  patientName ? `סיכום טיפולים – ${patientName}` : "סיכום טיפולים";

const messageFor = (error, fallback) => {
  const msg = error?.message || "";
  // Cloud Functions surface our Hebrew HttpsError messages verbatim.
  if (/[֐-׿]/.test(msg)) return msg;
  return fallback;
};

function AiSummaryPage() {
  const { patientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const reportId = searchParams.get("reportId") || null;
  const navigate = useNavigate();

  const clinicId = useSelector(selectClinicId);
  const clinic = useSelector(selectClinicProfile);
  const therapistName = useSelector(selectTherapistName);
  const canExportData = useSelector(selectCanExportData);
  const canViewSensitive = useSelector(selectHasPermission("clinical.viewSensitive"));
  const isAdmin = useSelector(selectIsAdmin);
  const isTrialExpired = useSelector(selectIsTrialExpired);
  const { checkAI, PlanLimitModalComponent } = usePlanPermissions();

  const session = usePdfExportSession({
    signatureErrorCode: "CK-3974",
    prefsErrorCode: "CK-3981",
  });
  const { therapistId, therapistProfile, savedSignatureUrl, options, setOptions } = session;

  const state = useAiSummaryState({
    clinicId,
    patientId,
    seedFromReport: !!reportId,
    options,
    setOptions,
  });

  // ─── Data ───
  const { data: patient, isLoading: patientLoading } = useGetPatientQuery(
    { clinicId, patientId },
    { skip: !clinicId || !patientId }
  );
  const canListAll = useCanListAllPatientTreatments(patient);
  // Same args as PatientProfilePage → shared RTK cache entry.
  const { data: rawTreatments, isLoading: treatmentsLoading } = useGetTreatmentsForPatientQuery(
    { clinicId, patientId, therapistId: canListAll ? null : therapistId },
    { skip: !clinicId || !patientId || !patient || !canViewSensitive }
  );
  const { data: treatmentTypes = [] } = useGetCategoriesQuery(
    { clinicId, type: "treatmentType" },
    { skip: !clinicId }
  );
  const typeNameById = useMemo(() => {
    const map = new Map();
    treatmentTypes.forEach((t) => map.set(t.id, t.name));
    return map;
  }, [treatmentTypes]);

  const treatments = useMemo(
    () =>
      (rawTreatments || [])
        .filter((t) => t.isDeleted !== true && t.status === "locked")
        .slice()
        .sort((a, b) => new Date(a.treatmentDate).getTime() - new Date(b.treatmentDate).getTime()),
    [rawTreatments]
  );

  // Default: everything selected, once the list lands — unless a draft / seed /
  // the user already picked.
  const defaultedRef = useRef(false);
  useEffect(() => {
    if (!rawTreatments || defaultedRef.current) return;
    defaultedRef.current = true;
    state.defaultSelection(treatments.map((t) => t.id));
  }, [rawTreatments, treatments, state]);

  // ─── Re-edit seed (?reportId=) ───
  const {
    data: seedReport,
    isLoading: seedLoading,
    isError: seedError,
  } = useGetGeneratedReportQuery({ clinicId, reportId }, { skip: !clinicId || !reportId });
  const [seedNotice, setSeedNotice] = useState(null);
  const seededRef = useRef(null);
  useEffect(() => {
    if (!reportId || seedLoading || seededRef.current === reportId) return;
    if (seedError || seedReport === undefined) {
      if (!seedError) return;
      seededRef.current = reportId;
      console.error("[CK-3976] Cannot reopen generated report for editing:", {
        errorCode: "CK-3976",
        clinicId,
        reportId,
        reason: "read failed (permission or missing)",
        timestamp: new Date().toISOString(),
      });
      setSeedNotice("הדוח לא זמין לעריכה — נפתח סיכום חדש.");
      setSearchParams({}, { replace: true });
      return;
    }
    seededRef.current = reportId;
    const r = seedReport;
    const valid =
      r &&
      r.reportTypeId === REPORT_TYPE_ID &&
      r.patientId === patientId &&
      r.editable === true &&
      typeof r.contentHtml === "string" &&
      r.contentHtml.trim();
    if (!valid) {
      console.error("[CK-3976] Cannot reopen generated report for editing:", {
        errorCode: "CK-3976",
        clinicId,
        reportId,
        reason: !r ? "missing" : "type/patient mismatch or no contentHtml",
        timestamp: new Date().toISOString(),
      });
      setSeedNotice("לא ניתן לפתוח את הדוח לעריכה — הטקסט המקורי לא נשמר. נפתח סיכום חדש.");
      setSearchParams({}, { replace: true });
      return;
    }
    state.seedFromExportedReport(r);
    if (r.options && typeof r.options === "object") {
      const seeded = mergePrefs({ ...therapistProfile?.treatmentPdfPrefs, ...r.options });
      setOptions((prev) => ({
        ...prev,
        header: seeded.header,
        patient: seeded.patient,
        content: seeded.content,
        signature: r.signatureMode || prev.signature,
      }));
    }
  }, [
    reportId,
    seedLoading,
    seedError,
    seedReport,
    patientId,
    clinicId,
    state,
    setOptions,
    setSearchParams,
    therapistProfile,
  ]);

  // ─── Derived ───
  const patientName = patientFullName(patient, seedReport?.patientName);
  const selectedTreatments = treatments.filter((t) => state.selectedTreatmentIds.has(t.id));
  const selectedCount = selectedTreatments.length;
  const overLimit = selectedCount > MAX_SELECTABLE_TREATMENTS;
  const unavailableSelected = state.selectedTreatmentIds.size - selectedCount;
  const sessionDates = selectedTreatments.map((t) => t.treatmentDate);
  const generatedAt = formatHebrewDate(new Date());
  const licenseNumber = resolveLicenseNumber(therapistProfile, clinic);
  const hasLicense = !!licenseNumber;
  const exporterName = therapistFullName(therapistProfile);
  const aiFeatureEnabled =
    getEffectiveLimits(clinic?.plan, clinic || {}).aiTreatmentsReportEnabled !== false;

  const normalized = useMemo(() => normalizeOptions(options), [options]);
  const docTitle = trim(options.title) || defaultTitleFor(patientName);

  const model = useMemo(() => {
    const o = normalized;
    const first = sessionDates[0];
    const last = sessionDates[sessionDates.length - 1];
    const rangeValue =
      first && last
        ? sessionDates.length === 1
          ? formatHebrewDate(first)
          : `${formatHebrewDate(first)} – ${formatHebrewDate(last)} · ${sessionDates.length} פגישות`
        : "";
    return {
      letterhead: buildLetterheadModel({
        clinic,
        header: o.header,
        authorName: exporterName,
        licenseNumber,
        generatedAt,
      }),
      addressee: trim(o.addressee),
      title: docTitle,
      patientRows: buildPatientRows({
        patientToggles: o.patient,
        patientName,
        patientDoc: patient,
        dateLabel: "טווח הטיפולים",
        dateValue: rangeValue,
        typeName: "",
        authorName: exporterName,
      }),
      sessionChips: o.content.sessionsList
        ? sessionDates.map((d) => ({ text: formatHebrewDate(d) }))
        : [],
      bodyHtml: state.summaryOk ? sanitizeFieldHtml(state.summaryHtml) : "",
      signature: buildSignatureModel({
        therapist: therapistProfile,
        clinic,
        signatureMode: o.signature,
        licenseNumber,
        generatedAt,
      }),
      footerNote: trim(o.footerNote),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalized, clinic, exporterName, licenseNumber, generatedAt, docTitle, patientName, patient, state.summaryHtml, state.summaryOk, therapistProfile, selectedCount, treatments]);

  // ─── Generate ───
  const [generate, { isLoading: isGenerating }] = useGenerateTreatmentsReportSummaryMutation();
  const [generateError, setGenerateError] = useState(null);
  const handleGenerate = useCallback(async () => {
    setGenerateError(null);
    const allowed = await checkAI();
    if (!allowed) return;
    const treatmentIds = selectedTreatments.map((t) => t.id);
    try {
      const result = await generate({
        clinicId,
        patientId,
        treatmentIds,
        therapistNotes: state.aiNotes,
      }).unwrap();
      state.applySummary(result.summaryHtml, {
        treatmentsUsed: result.treatmentsUsed,
        truncated: result.truncated,
        dropped: result.dropped,
      });
      // A fresh AI draft is a new document, not a revision of an old export.
      state.setEditingReport(null);
      if (reportId) setSearchParams({}, { replace: true });
      logActivity(clinicId, {
        therapistId,
        therapistName: therapistName || "לא ידוע",
        action: ACTIVITY_ACTIONS.GENERATE_AI_TREATMENTS_SUMMARY,
        entityType: ENTITY_TYPES.PATIENT,
        entityId: patientId,
        entityName: patientName,
        details: { treatmentCount: treatmentIds.length, hasNotes: !!state.aiNotes.trim() },
      });
    } catch (error) {
      console.error("[CK-3972] Error generating AI treatments summary:", {
        errorCode: "CK-3972",
        errorMessage: error?.message,
        clinicId,
        treatmentCount: treatmentIds.length,
        timestamp: new Date().toISOString(),
      });
      setGenerateError(messageFor(error, "אירעה שגיאה ביצירת הסיכום. נסו שוב בעוד רגע."));
      logActivity(clinicId, {
        therapistId,
        therapistName: therapistName || "לא ידוע",
        action: ACTIVITY_ACTIONS.GENERATE_AI_TREATMENTS_SUMMARY,
        entityType: ENTITY_TYPES.PATIENT,
        entityId: patientId,
        entityName: patientName,
        success: false,
        errorMessage: error?.message || null,
        details: { treatmentCount: treatmentIds.length },
      });
    }
  }, [checkAI, selectedTreatments, generate, clinicId, patientId, state, reportId, setSearchParams, therapistId, therapistName, patientName]);

  // ─── Export ───
  const [generatePdf, { isLoading: isExporting }] = useGenerateTreatmentsSummaryPdfMutation();
  const [exportError, setExportError] = useState(null);
  const runExport = useCallback(async () => {
    setExportError(null);
    session.setPdfUrl(null);
    const o = normalized;
    const treatmentIds = selectedTreatments.map((t) => t.id);
    const sourceReportId = state.editingReport?.id || null;
    const details = {
      reportTypeId: REPORT_TYPE_ID,
      treatmentCount: treatmentIds.length,
      signatureMode: o.signature,
      hasFooterNote: !!o.footerNote.trim(),
      revisionOf: sourceReportId,
    };
    const baseLog = {
      therapistId,
      therapistName: therapistName || "לא ידוע",
      action: ACTIVITY_ACTIONS.GENERATE_PATIENT_REPORT,
      entityType: ENTITY_TYPES.PATIENT,
      entityId: patientId,
      entityName: patientName,
    };
    try {
      const result = await generatePdf({
        clinicId,
        patientId,
        patientName,
        title: docTitle,
        subtitle: o.addressee.trim(),
        contentHtml: state.summaryHtml,
        footerNote: o.footerNote.trim(),
        treatmentIds,
        // Legacy flag kept one release so an older deployed function still signs.
        includeSignature: o.signature === "saved",
        sourceReportId,
        options: o,
      }).unwrap();
      session.setPdfUrl(result.pdfUrl);
      state.clearDraft();
      if (result.reportId) {
        state.setEditingReport({
          id: result.reportId,
          createdAt: new Date().toISOString(),
          revision: (state.editingReport?.revision || 0) + 1,
        });
        seededRef.current = result.reportId; // don't re-seed from our own export
        setSearchParams({ reportId: result.reportId }, { replace: true });
      }
      logActivity(clinicId, { ...baseLog, details });
      session.rememberPrefs(o);
    } catch (error) {
      console.error("[CK-3973] Error exporting AI treatments summary PDF:", {
        errorCode: "CK-3973",
        errorMessage: error?.message,
        clinicId,
        treatmentCount: treatmentIds.length,
        signatureMode: o.signature,
        revisionOf: sourceReportId,
        timestamp: new Date().toISOString(),
      });
      setExportError(messageFor(error, "אירעה שגיאה בהפקת הדוח. נסו שוב בעוד רגע."));
      logActivity(clinicId, { ...baseLog, success: false, errorMessage: error?.message || null, details });
    }
  }, [normalized, selectedTreatments, state, therapistId, therapistName, patientId, patientName, generatePdf, clinicId, docTitle, session, setSearchParams]);

  const canExportNow =
    canExportData && state.summaryOk && selectedCount > 0 && !overLimit && !isTrialExpired;

  const handleExportClick = () => {
    if (isExporting || session.isSavingSignature || !canExportNow) return;
    if (options.signature === "saved" && !savedSignatureUrl) {
      session.openPad();
      return;
    }
    runExport();
  };
  const handlePadConfirm = async (dataUrl) => {
    session.closePad();
    const ok = await session.saveSignature(dataUrl);
    if (ok && options.signature === "saved") runExport();
  };

  const handleReset = () => {
    state.reset();
    setGenerateError(null);
    setExportError(null);
    setSeedNotice(null);
    session.setPdfUrl(null);
    seededRef.current = null;
    if (reportId) setSearchParams({}, { replace: true });
  };

  const [focusMode, setFocusMode] = useState(false);

  // ─── Guards ───
  const isLoading = !clinicId || patientLoading || (!!patient && treatmentsLoading && !rawTreatments);
  const guard = isLoading
    ? null
    : !canViewSensitive
      ? { Icon: WarningCircle, title: "אין הרשאה", text: "הרשאה נדרשת: גישה למידע רפואי רגיש." }
      : !aiFeatureEnabled
        ? { Icon: WarningCircle, title: "סיכום AI אינו זמין", text: "האפשרות אינה פעילה בחשבון הקליניקה. פנו לתמיכה." }
        : !patient
          ? { Icon: FileX, title: "המטופל/ת לא נמצא/ה", text: "ייתכן שאין לך גישה לכרטיס זה." }
          : treatments.length === 0
            ? {
                Icon: FileX,
                title: "אין תיעודי טיפול נעולים לסיכום",
                text: "ניתן לסכם רק תיעודי טיפול שננעלו. נעלו תיעוד אחד לפחות ונסו שוב.",
              }
            : null;

  const patientPath = `/dashboard/patients/${patientId}`;
  const editingSince = state.editingReport?.createdAt
    ? formatHebrewDate(state.editingReport.createdAt)
    : null;
  const footerError = exportError || session.signatureError;

  const sidebar = (
    <div className="pdx-sidebar">
      {seedNotice && (
        <div className="ais-notice warn" role="status">
          <WarningCircle size={15} weight="bold" />
          {seedNotice}
        </div>
      )}
      {state.editingReport && (
        <div className="ais-notice" role="status">
          <PencilSimpleLine size={15} weight="duotone" />
          <span>
            <strong>עריכת דוח שכבר הופק</strong>
            {editingSince ? ` · גרסה ${state.editingReport.revision || 1} מ-${editingSince}` : ""}
            {" · "}
            ייצוא מחדש יפיק PDF חדש; הגרסה הקודמת נשמרת בהיסטוריה.
          </span>
        </div>
      )}
      {state.restoredNotice && (
        <div className="ais-notice" role="status">
          <ArrowCounterClockwise size={15} weight="bold" />
          שוחזרה טיוטה שלא יוצאה. אפשר להמשיך לערוך או ליצור מחדש.
        </div>
      )}

      <Group
        title="בחירת טיפולים"
        action={
          unavailableSelected > 0 ? (
            <span className="pdx-toggle-hint">{selectedCount} מתוך {state.selectedTreatmentIds.size} טיפולים זמינים</span>
          ) : null
        }
      >
        <TreatmentsTimelinePicker
          treatments={treatments}
          selectedIds={state.selectedTreatmentIds}
          onChange={state.setSelectedTreatmentIds}
          typeNameById={typeNameById}
        />
      </Group>

      <Group title="הנחיות ל-AI">
        <AiNotesPanel
          aiNotes={state.aiNotes}
          setAiNotes={state.setAiNotes}
          summaryOk={state.summaryOk}
          summaryMeta={state.summaryMeta}
          selectedCount={selectedCount}
          overLimit={overLimit}
          isGenerating={isGenerating}
          generateError={generateError}
          onGenerate={handleGenerate}
        />
      </Group>

      <Group title="עריכת הטקסט">
        {isGenerating && !state.summaryOk ? (
          <ContentLoader message="ה-AI כותב את הסיכום…" compact />
        ) : (
          <SummaryEditor
            value={state.summaryHtml}
            resetKey={state.summaryVersion}
            onChange={state.setSummaryHtml}
            fullscreen={focusMode}
            onToggleFullscreen={() => setFocusMode((f) => !f)}
          />
        )}
      </Group>

      <Group title="מסמך">
        <TextField
          id="ais-title"
          label="כותרת המסמך"
          value={options.title}
          onChange={(v) => session.setText("title", v)}
          max={TEXT_LIMITS.title}
          placeholder={defaultTitleFor(patientName)}
        />
        <TextField
          id="ais-addressee"
          label="לכבוד (נמען)"
          value={options.addressee}
          onChange={(v) => session.setText("addressee", v)}
          max={TEXT_LIMITS.addressee}
          placeholder="לדוגמה: ועדת השמה · ההורים · הרופא המטפל"
        />
      </Group>

      <LetterheadToggles
        header={options.header}
        onToggle={(key) => session.toggleOption("header", key)}
        clinic={clinic}
        hasLicense={hasLicense}
        isAdmin={isAdmin}
      />

      <PatientToggles
        patient={options.patient}
        onToggle={(key) => session.toggleOption("patient", key)}
        patientDoc={patient}
        rows={{ treatmentDate: true, treatmentType: false }}
        dateLabel="טווח הטיפולים"
        extra={
          <ToggleRow
            label="רשימת פגישות שנכללו"
            checked={options.content.sessionsList}
            disabled={selectedCount === 0}
            hint={selectedCount === 0 ? "בחרו טיפולים" : "תאריכי הפגישות מתחת לפרטי המטופל"}
            onChange={() => session.toggleOption("content", "sessionsList")}
          />
        }
      />

      <Group title="חתימה">
        <SignatureSegment
          value={options.signature}
          onChange={(v) => session.setOption(null, "signature", v)}
          savedSignatureUrl={savedSignatureUrl}
          onDrawSignature={session.openPad}
        />
      </Group>

      <Group title="הערות והסתייגויות">
        <TextField
          id="ais-footer"
          label="הערות והסתייגויות לתחתית הדוח"
          value={options.footerNote}
          onChange={(v) => session.setText("footerNote", v)}
          max={TEXT_LIMITS.footerNote}
          multiline
          rows={3}
          hint="יופיע באפור, מתחת לקו, בסוף המסמך — למשל מגבלות הסיכום או הצהרת סודיות."
          placeholder="לדוגמה: הסיכום נכתב לבקשת ההורים ומבוסס על התרשמות קלינית בלבד; אין לראות בו אבחנה."
        />
      </Group>

      <button
        type="button"
        className="prw-btn ghost ais-reset-btn"
        onClick={handleReset}
        disabled={isExporting || isGenerating}
        title="התחלת סיכום חדש"
      >
        <ArrowCounterClockwise size={15} />
        סיכום חדש
      </button>
    </div>
  );

  return (
    <PdfExportShell
      className="ais-page"
      icon={Sparkle}
      title="סיכום סדרת טיפולים בעזרת AI"
      subtitle={
        patient
          ? [patientName, treatments.length ? `${treatments.length} תיעודים נעולים` : null]
              .filter(Boolean)
              .join(" · ")
          : null
      }
      onBack={() => navigate(patientPath)}
      backLabel="חזרה לכרטיס המטופל"
      loading={isLoading}
      loadingMessage="טוען את תיעודי הטיפול..."
      guard={guard}
      sidebar={sidebar}
      preview={<AiSummaryPreview model={model} />}
      footer={
        <>
          <div className="pdx-footer-text">
            {footerError ? (
              <span className="pdx-footer-error">
                <WarningCircle size={15} weight="bold" />
                {footerError}
              </span>
            ) : isTrialExpired ? (
              <span className="pdx-footer-error">תקופת הניסיון הסתיימה — הייצוא אינו זמין.</span>
            ) : !canExportData ? (
              <span>ייצוא נתונים אינו מופעל עבורך — אפשר ליצור ולערוך, אך לא לייצא.</span>
            ) : !state.summaryOk ? (
              <span>צרו סיכום עם AI או כתבו טקסט בעורך כדי לייצא.</span>
            ) : (
              <span>
                {selectedCount} טיפולים · ה-PDF יישמר גם בהיסטוריית הדוחות (דוחות › ייצוא דוחות).
              </span>
            )}
          </div>
          {canExportData && (
            <button
              type="button"
              className="prw-btn primary pdx-export-btn"
              onClick={handleExportClick}
              disabled={!canExportNow || isExporting || session.isSavingSignature}
            >
              {isExporting ? <LoaderLoop size="xs" /> : <FilePdf size={16} weight="fill" />}
              {isExporting ? "מפיק PDF…" : state.editingReport ? "ייצוא מחדש" : "ייצוא PDF"}
            </button>
          )}
        </>
      }
    >
      {session.padOpen && (
        <SignaturePadModal
          title="החתימה שלך"
          hint="חתמו באמצעות העכבר או מסך מגע. החתימה תישמר בפרופיל שלך לשימוש חוזר."
          confirmLabel="שמירה והמשך"
          onConfirm={handlePadConfirm}
          onClose={session.closePad}
        />
      )}
      {session.pdfUrl && (
        <PdfPreviewModal
          url={session.pdfUrl}
          title={docTitle}
          onClose={() => session.setPdfUrl(null)}
        />
      )}
      <PlanLimitModalComponent />
    </PdfExportShell>
  );
}

export default AiSummaryPage;
